import { Drawer, notification, Spin, Tag } from "antd";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { BiChevronLeft } from "react-icons/bi";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "shared/components/Header";
import {
  PayloadPathsProvider,
  toPayloadPathOptions,
} from "shared/components/PayloadPaths";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { SCREENS } from "utils/constants";
import { NAME_SPACES } from "shared/locales/constants";
import { CREATE_STEPS, STEPS } from "../Create/constants";
import StepAnchors from "../Create/StepAnchors";
import StepDetails from "../Create/StepDetails";
import { PREVIEW_PAYLOAD } from "../previewPayload";
import StepUpload from "./StepUpload";

const Edit = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS);
  const EDIT = t("EDIT", { returnObjects: true });
  const STEP_LABELS = t("STEPS", { returnObjects: true });
  const { id } = useParams();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(
    STEPS.indexOf(CREATE_STEPS.ANCHORS),
  );
  const [file, setFile] = useState(null); // null = keep existing
  const [anchors, setAnchors] = useState([]);
  const [mappings, setMappings] = useState({});
  const [name, setName] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  // Payload paths the last preview asked for but could not resolve.
  const [unresolved, setUnresolved] = useState([]);

  const { data, loading: loadingTemplate } = useQuery(
    graphql.queries.DOCUMENT_TEMPLATE,
    { variables: { where: { id } }, skip: !id },
  );

  const template = data?.documentTemplate;

  // The field catalog is introspected from a real subscription rather than
  // hard-coded, so it always matches the payload the template will receive.
  const [loadPayloadSchema, { data: schemaData, loading: schemaLoading }] =
    useLazyQuery(graphql.queries.DOCUMENT_PAYLOAD_SCHEMA);
  const payloadPaths = useMemo(
    () => toPayloadPathOptions(schemaData?.documentPayloadSchema?.paths),
    [schemaData],
  );
  const schemaPayload = schemaData?.documentPayloadSchema?.payload || null;

  // Pre-populate once the template is loaded
  useEffect(() => {
    if (!template) return;
    setAnchors(template.anchors || []);
    setMappings(template.anchorMappings || {});
    setName(template.name || "");
  }, [template]);

  const [previewDocumentTemplate, { loading: previewing }] = useMutation(
    graphql.mutations.PREVIEW_DOCUMENT_TEMPLATE,
    {
      onCompleted: (data) => {
        setUnresolved(data?.previewDocumentTemplate?.unresolved || []);
        const b64 = data?.previewDocumentTemplate?.base64;
        if (!b64) return;
        const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      },
      onError: () =>
        notification.error({
          message: EDIT.PREVIEW_ERROR,
        }),
    },
  );

  const [updateDocumentTemplate, { loading: saving }] = useMutation(
    graphql.mutations.UPDATE_DOCUMENT_TEMPLATE,
    {
      refetchQueries: [
        {
          query: graphql.queries.DOCUMENT_TEMPLATES,
          awaitRefetchQueries: true,
        },
        {
          query: graphql.queries.DOCUMENT_TEMPLATE,
          variables: { where: { id } },
          awaitRefetchQueries: true,
        },
      ],
      onCompleted: () =>
        navigate(SCREENS.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS.LIST.path),
      onError: () =>
        notification.error({
          message: EDIT.ERROR,
        }),
    },
  );

  const handleUploadDone = ({ file: uploadedFile, anchors: detected }) => {
    setFile(uploadedFile);
    setAnchors(detected);
    // Preserve mappings for anchors that are still present in the new file.
    // Anchors that disappeared are silently dropped; new ones start unmapped.
    setMappings((prev) => {
      const next = {};
      for (const anchor of detected) {
        if (prev[anchor] !== undefined) next[anchor] = prev[anchor];
      }
      return next;
    });
    setCurrentStep(STEPS.indexOf(CREATE_STEPS.ANCHORS));
  };

  const handleKeepFile = () => {
    setCurrentStep(STEPS.indexOf(CREATE_STEPS.ANCHORS));
  };

  const handleSave = () => {
    const variables = {
      where: { id },
      data: {
        name,
        anchorMappings: mappings,
        anchors,
        ...(file
          ? {
              file: {
                content: file.content,
                name: file.name,
                contentType: file.contentType,
              },
            }
          : {}),
      },
    };
    updateDocumentTemplate({ variables });
  };

  const handlePreview = (mockPayload) => {
    // Use the user-supplied mock payload when at least one value is filled;
    // fall back to the built-in PREVIEW_PAYLOAD otherwise.
    const payload =
      mockPayload && Object.keys(mockPayload).length > 0
        ? mockPayload
        : PREVIEW_PAYLOAD;
    previewDocumentTemplate({
      variables: {
        where: { id },
        mappings,
        payload,
      },
    });
  };

  const goBack = () => {
    if (currentStep === 0) {
      navigate(SCREENS.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS.LIST.path);
      return;
    }
    setCurrentStep((s) => s - 1);
  };

  const stepName = STEPS[currentStep];

  if (loadingTemplate) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin />
      </div>
    );
  }

  return (
    <>
      <Header
        title={EDIT.HEADER.TITLE}
        actions={[
          {
            text: EDIT.HEADER.BACK,
            onClick: () =>
              navigate(SCREENS.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS.LIST.path),
            type: "gray--link",
            icon: <BiChevronLeft size={20} />,
          },
        ]}
      />

      <div className="pricers-import__stepper">
        {STEPS.map((step, idx) => {
          const isActive = idx === currentStep;
          const isDone = idx < currentStep;
          return (
            <div key={step} className="pricers-import__step-item">
              <div className="pricers-import__step-row">
                <div
                  className={classNames("pricers-import__step-connector", {
                    "pricers-import__step-connector--done": isDone || isActive,
                    "pricers-import__step-connector--hidden": idx === 0,
                  })}
                />
                <span
                  className={classNames("pricers-import__step-number", {
                    "pricers-import__step-number--active": isActive,
                    "pricers-import__step-number--done": isDone,
                  })}
                >
                  {isDone ? "✓" : idx + 1}
                </span>
                <div
                  className={classNames("pricers-import__step-connector", {
                    "pricers-import__step-connector--done": isDone,
                    "pricers-import__step-connector--hidden":
                      idx === STEPS.length - 1,
                  })}
                />
              </div>
              <span
                className={classNames("pricers-import__step-label", {
                  "pricers-import__step-label--active": isActive,
                  "pricers-import__step-label--done": isDone,
                })}
              >
                {STEP_LABELS[step]}
              </span>
            </div>
          );
        })}
      </div>

      {stepName === CREATE_STEPS.UPLOAD && (
        <StepUpload
          currentS3Key={template?.s3Key}
          onNext={handleUploadDone}
          onKeep={handleKeepFile}
          onBack={goBack}
        />
      )}

      {stepName === CREATE_STEPS.ANCHORS && (
        <PayloadPathsProvider paths={payloadPaths}>
          <StepAnchors
            anchors={anchors}
            mappings={mappings}
            onMappingChange={setMappings}
            onNext={() => setCurrentStep(STEPS.indexOf(CREATE_STEPS.DETAILS))}
            onBack={goBack}
            onPreview={handlePreview}
            previewing={previewing}
            file={file}
            unresolved={unresolved}
            schemaLoading={schemaLoading}
            schemaPayload={schemaPayload}
            templateName={name}
            onLoadPayloadSchema={(subscriptionId) =>
              loadPayloadSchema({
                variables: { where: { id: subscriptionId } },
              })
            }
          />
        </PayloadPathsProvider>
      )}

      {stepName === CREATE_STEPS.DETAILS && (
        <StepDetails
          name={name}
          onNameChange={setName}
          onBack={goBack}
          onSave={handleSave}
          loading={saving}
        />
      )}
      <Drawer
        open={!!previewUrl}
        onClose={() => {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }}
        width="90vw"
        title={EDIT.PREVIEW_FRAME}
        destroyOnClose
        styles={{
          header: { flexShrink: 0 },
          body: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
          wrapper: { height: "100%" },
        }}
      >
        {previewUrl && (
          <iframe
            src={previewUrl}
            title={EDIT.PREVIEW_TITLE}
            style={{
              flex: 1,
              width: "100%",
              height: "100%",
              border: "none",
              display: "block",
            }}
          />
        )}
      </Drawer>
    </>
  );
};

export default Edit;
